'use client';
import { useEffect, useState } from 'react';
import { type BreakpointKey, getBreakpointPixels } from './useBreakpoint';

export type UseContainerQueryOptions = {
  /** Value returned before the element has been measured. Defaults to `false`. */
  defaultValue?: boolean;
};

/** Container-query equivalent of `useBreakpoint`: matches one element's own width instead of the viewport. */
export function useContainerQuery<T extends Element>(
  ref: React.RefObject<T | null>,
  key: BreakpointKey,
  type: 'up' | 'down' = 'up',
  { defaultValue = false }: UseContainerQueryOptions = {},
): boolean {
  const [matches, setMatches] = useState(defaultValue);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const threshold = getBreakpointPixels(key);
    const evaluate = (width: number) => (type === 'up' ? width >= threshold : width <= threshold);

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      // width 0 means hidden (e.g. a collapsed `w-0` panel), not narrower than every breakpoint.
      if (width === 0) return;
      setMatches(evaluate(width));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, key, type]);

  return matches;
}
